import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime } from '../ui/format';
import { Badge, EmptyState, Loading } from '../ui/Page';
import {
  connectCalendar,
  disconnectCalendar,
  listCalendars,
  listConnections,
  setSyncDirection,
  type SyncDirection,
} from './api';

/**
 * Connecting somebody's own Google calendar.
 *
 * The screen is honest about where this stands. The connection, the target calendar, the direction
 * and the disconnect are real and work end to end; what is not connected yet is Google itself,
 * because the OAuth client is a credential this repository must not carry. So a connection made here
 * sits in `PENDING` — "aguardando autorização" — and the page says so rather than pretending to be
 * one click from working.
 *
 * That honesty is the point. A screen that showed a green tick and then quietly synced nothing would
 * be worse than no screen, and a customer would find out from an empty week.
 */
export function SyncPanel(): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState('');

  const connections = useQuery({ queryKey: ['calendar-connections'], queryFn: listConnections });
  const calendars = useQuery({ queryKey: ['calendars'], queryFn: listCalendars });

  // Only a calendar this person may write to can receive somebody else's events.
  const writable = (calendars.data?.calendars ?? []).filter(
    (calendar) => calendar.access === 'WRITE' || calendar.access === 'MANAGE',
  );

  const refresh = (): void => {
    setError(null);
    void client.invalidateQueries({ queryKey: ['calendar-connections'] });
  };

  const connect = useMutation({
    mutationFn: () =>
      connectCalendar({ provider: 'google', calendarId: target || writable[0]!.id }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const direction = useMutation({
    mutationFn: (input: { id: string; direction: SyncDirection }) =>
      setSyncDirection(input.id, input.direction),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => disconnectCalendar(id),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (connections.isLoading) return <Loading />;

  const rows = connections.data?.connections ?? [];
  const busy = connect.isPending || direction.isPending || remove.isPending;

  return (
    <div className="stack">
      <p className="muted small">{t('calendar.syncDescription')}</p>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title={t('calendar.noConnections')}
          description={t('calendar.noConnectionsBody')}
        />
      ) : (
        <ul className="home-list">
          {rows.map((connection) => (
            <li key={connection.id}>
              <span className="cell-inline">
                <span className="strong">{t(`calendar.provider.${connection.provider}`)}</span>

                {connection.accountEmail && (
                  <span className="muted small">{connection.accountEmail}</span>
                )}

                <Badge
                  tone={
                    connection.status === 'ACTIVE'
                      ? 'positive'
                      : connection.status === 'PENDING'
                        ? 'warning'
                        : 'danger'
                  }
                >
                  {t(`calendar.syncStatus.${connection.status}`)}
                </Badge>

                <select
                  className="select"
                  value={connection.direction}
                  disabled={busy}
                  onChange={(changeEvent) =>
                    direction.mutate({
                      id: connection.id,
                      direction: changeEvent.target.value as SyncDirection,
                    })
                  }
                >
                  {(['BOTH', 'PULL', 'PUSH'] as const).map((value) => (
                    <option key={value} value={value}>
                      {t(`calendar.direction.${value}`)}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className="button button--quiet button--compact"
                  disabled={busy}
                  onClick={() => remove.mutate(connection.id)}
                >
                  {t('calendar.disconnect')}
                </button>
              </span>

              <span className="muted small">
                {connection.lastSyncedAt
                  ? t('calendar.lastSynced').replace(
                      '{when}',
                      formatDateTime(connection.lastSyncedAt),
                    )
                  : t('calendar.neverSynced')}
                {connection.lastError ? ` · ${t(`calendar.error.${connection.lastError}`)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="cell-inline">
        <label className="field">
          <span>{t('calendar.syncInto')}</span>
          <select
            className="select"
            value={target || (writable[0]?.id ?? '')}
            disabled={busy || writable.length === 0}
            onChange={(changeEvent) => setTarget(changeEvent.target.value)}
          >
            {writable.map((calendar) => (
              <option key={calendar.id} value={calendar.id}>
                {calendar.name}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="button button--compact"
          disabled={busy || writable.length === 0}
          onClick={() => connect.mutate()}
        >
          {t('calendar.connectGoogle')}
        </button>
      </div>

      {/* Said plainly, once, rather than discovered later from an empty week. */}
      <p className="muted small">{t('calendar.syncPending')}</p>
    </div>
  );
}
